<template>
  <div
    @mouseover="showFullText"
    @mouseout="hideFullTextOnLeave"
    @focusin="showFullText"
    @focusout="hideFullText"
    @keydown.esc="handleEscape"
  >
    <slot />
    <Teleport to="body">
      <div
        v-if="fullText"
        role="tooltip"
        class="truncated-text-reveal"
        :style="fullText.style"
        @mouseleave="hideFullText"
      >
        {{ fullText.text }}
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { useEventListener } from "@vueuse/core";
import { ref } from "vue";

// Overlays the full text of any clipped `.text-truncate` element inside it while the pointer or
// focus is on that element; text that fits is left alone. Unlike a title tooltip it shows at once,
// in the element's own font, and its text can be selected. Give an element `tabindex="0"` to make it
// revealable from the keyboard.

const fullText = ref<{ text: string; style: Record<string, string> } | null>(
  null
);

const hideFullText = () => {
  fullText.value = null;
};

// Escape closes the reveal alone, not also a modal the table sits in.
const handleEscape = (event: KeyboardEvent) => {
  if (fullText.value) {
    event.stopPropagation();
    hideFullText();
  }
};

const hideFullTextOnLeave = (event: MouseEvent) => {
  if (
    event.relatedTarget instanceof Element &&
    event.relatedTarget.closest(".truncated-text-reveal")
  )
    return;
  hideFullText();
};

const showFullText = (event: Event) => {
  const target = event.target;
  if (
    !(target instanceof HTMLElement) ||
    !target.classList.contains("text-truncate") ||
    target.scrollWidth <= target.clientWidth
  ) {
    hideFullText();
    return;
  }

  const bounds = target.getBoundingClientRect();
  const left = Math.max(8, bounds.left - 8);
  const top = Math.max(8, bounds.top - 4);
  fullText.value = {
    text: target.textContent?.trim() ?? "",
    style: {
      left: `${left}px`,
      top: `${top}px`,
      maxWidth: `${window.innerWidth - left - 8}px`,
      maxHeight: `${window.innerHeight - top - 8}px`,
      font: getComputedStyle(target).font,
    },
  };
};

// A virtual row can be recycled while its text is revealed.
useEventListener(
  window,
  "scroll",
  (event) => {
    if (
      event.target instanceof Element &&
      event.target.closest(".truncated-text-reveal")
    )
      return;
    hideFullText();
  },
  true
);
useEventListener(window, "resize", hideFullText);

defineExpose({ hide: hideFullText });
</script>

<style scoped lang="scss">
.truncated-text-reveal {
  position: fixed;
  z-index: 1100;
  width: max-content;
  padding: 4px 8px;
  overflow-y: auto;
  overflow-wrap: anywhere;
  white-space: normal;
  font-size: 0.875rem;
  color: var(--phoenix-body-color);
  background: var(--phoenix-body-bg);
  border-radius: 3px;
  box-shadow:
    0 0 0 1px var(--phoenix-border-color),
    0 2px 6px #0002;
}
</style>
